import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Badge,
  Input,
} from "reactstrap";
import { Users, X, Trash2 } from "react-feather";
import axios from "axios";
import { baseURL } from "../../../../../Services/api/baseURL";
import { toast } from "react-toastify";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal1";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ViewTagCustomersModal = ({ isOpen, toggle, tag, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customerToRemove, setCustomerToRemove] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && tag) {
      fetchCustomers();
      setSelectedCustomers([]);
    }
  }, [isOpen, tag]);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.get(
        `${baseURL}/api/tags/${tag._id}/customers`,
        {
          params: { limit: 100000 },
          headers: { Authorization: token },
        }
      );

      if (response.status === 200) {
        setCustomers(response.data.data.customers);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCustomer = async (customer) => {
    setCustomerToRemove(customer);
    setConfirmModalOpen(true);
  };

  const confirmRemoveCustomer = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      await axios.post(
        `${baseURL}/api/tags/customers/${customerToRemove._id}/remove`,
        { tagIds: [tag._id] },
        { headers: { Authorization: token } }
      );

      toast.success("Customer removed from tag");
      setCustomers((prev) =>
        prev.filter((c) => c._id !== customerToRemove._id)
      );
      if (onSuccess) onSuccess();
      setConfirmModalOpen(false);
      setCustomerToRemove(null);
    } catch (error) {
      toast.error("Failed to remove customer");
      console.error(error);
    }
  };

  const handleBulkRemove = async () => {
    if (selectedCustomers.length === 0) {
      toast.error("Please select customers to remove");
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.post(
        `${baseURL}/api/tags/bulk/remove`,
        {
          customerIds: selectedCustomers,
          tagId: tag._id,
        },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        toast.success(
          `Tag removed from ${response.data.data.modified} customer(s)`
        );
        fetchCustomers();
        setSelectedCustomers([]);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error("Failed to remove customers");
      console.error(error);
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prev) => {
      if (prev.includes(customerId)) {
        return prev.filter((id) => id !== customerId);
      } else {
        return [...prev, customerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customers.map((c) => c._id));
    }
  };

  // 🔹 Export to Excel
  const handleExport = () => {
    if (customers.length === 0) {
      toast.error("No customers to export");
      return;
    }

    const exportData = customers.map((c) => ({
      Name:
        c.first_name && c.last_name
          ? `${c.first_name} ${c.last_name}`
          : c.first_name || c.last_name || "N/A",
      Email: c.email || "N/A",
      Phone: c.phoneNumber || "N/A",
      Tags: c.tags?.map((t) => t.display_name).join(", "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Customers_${tag.display_name}.xlsx`);
    toast.success("Exported successfully");
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="d-flex align-items-center gap-2">
              <Users size={20} />
              <span>
                Customers with "{tag?.display_name}"
                {!isLoading && (
                  <Badge color="light" className="ms-2 text-black">
                    {customers.length}
                  </Badge>
                )}
              </span>
            </div>

            {/* 🔹 Export Excel Button */}
            <Button  size="sm" className="ms-2 btn btn-primary d-flex align-items-center gap-1" onClick={handleExport}>
              <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="18"
                    viewBox="0 0 14 18"
                    fill="none"
                  >
                    <path
                      d="M9.33882 8.82071L9.33871 8.82059L9.32968 8.82962L8.125 10.0341V5.75L11.687 5.75C11.6871 5.75 11.6872 5.75 11.6874 5.75C12.1348 5.75058 12.5637 5.92857 12.8801 6.24494C13.1965 6.56141 13.3745 6.99049 13.375 7.43803V15.562C13.3745 16.0095 13.1965 16.4386 12.8801 16.7551C12.5636 17.0715 12.1345 17.2495 11.687 17.25H2.31303C1.86549 17.2495 1.43641 17.0715 1.11994 16.7551C0.803539 16.4387 0.625547 16.0097 0.625 15.5622V7.43778C0.625547 6.99032 0.80354 6.56135 1.11994 6.24494C1.43631 5.92857 1.86523 5.75058 2.31263 5.75C2.31277 5.75 2.3129 5.75 2.31303 5.75L5.875 5.75V10.0341L4.67032 8.82962L4.67043 8.8295L4.66118 8.82071C4.44849 8.61864 4.16527 8.50765 3.87192 8.51141C3.57855 8.51516 3.29827 8.63337 3.09082 8.84082C2.88337 9.04827 2.76516 9.32856 2.76141 9.62191C2.75765 9.91527 2.86864 10.1985 3.07071 10.4112L3.07059 10.4113L3.07965 10.4204L6.20465 13.5454L6.20477 13.5455C6.41572 13.7563 6.70176 13.8747 7 13.8747C7.29824 13.8747 7.58428 13.7563 7.79523 13.5455L7.79535 13.5454L10.9204 10.4204L10.9205 10.4205L10.9293 10.4112C11.1314 10.1985 11.2423 9.91527 11.2386 9.62191C11.2348 9.32855 11.1166 9.04827 10.9092 8.84082C10.7017 8.63337 10.4214 8.51516 10.1281 8.51141C9.83473 8.50765 9.55151 8.61864 9.33882 8.82071ZM7.08839 0.786612C7.11183 0.810053 7.125 0.841847 7.125 0.875V4.75H6.875V0.875C6.875 0.841849 6.88817 0.810054 6.91161 0.786612C6.93505 0.763169 6.96685 0.75 7 0.75C7.03315 0.75 7.06495 0.76317 7.08839 0.786612Z"
                      fill="white"
                      stroke="white"
                    />
                  </svg>
              Export Excel
            </Button>
          </div>
        </ModalHeader>

        <ModalBody>
          {selectedCustomers.length > 0 && (
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <Badge color="primary" style={{ fontSize: "0.9rem", padding: "8px 12px" }}>
                {selectedCustomers.length} selected
              </Badge>
              <Button color="danger" size="sm" onClick={handleBulkRemove}>
                <Trash2 size={14} className="me-1" />
                Remove Selected
              </Button>
            </div>
          )}

          {customers.length > 0 && (
            <div className="mb-3">
              <label>
                <Input
                  type="checkbox"
                  checked={customers.length > 0 && selectedCustomers.length === customers.length}
                  onChange={handleSelectAll}
                  className="me-2"
                />
                Select All
              </label>
            </div>
          )}

          <div
            style={{
              maxHeight: "500px",
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
            }}
          >
            {isLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No customers have this tag yet
              </div>
            ) : (
              customers.map((customer) => {
                const fullName =
                  customer.first_name && customer.last_name
                    ? `${customer.first_name} ${customer.last_name}`
                    : customer.first_name || customer.last_name || "N/A";
                const isSelected = selectedCustomers.includes(customer._id);

                return (
                  <div
                    key={customer._id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid #e0e0e0",
                      backgroundColor: isSelected ? "#e3f2fd" : "#fff",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectCustomer(customer._id)}
                        />
                        <div className="flex-grow-1">
                          <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                            {fullName}
                          </div>
                          <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                            {customer.email || "No email"}
                            {customer.phoneNumber && <> • {customer.phoneNumber}</>}
                          </div>
                          {customer.tags && customer.tags.length > 1 && (
                            <div className="mt-2">
                              {customer.tags
                                .filter((t) => t._id !== tag._id)
                                .slice(0, 3)
                                .map((t) => (
                                  <Badge
                                    key={t._id}
                                    style={{
                                      backgroundColor: t.color,
                                      color: "#fff",
                                      marginRight: "4px",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {t.display_name}
                                  </Badge>
                                ))}
                              {customer.tags.length > 4 && (
                                <Badge color="light">
                                  +{customer.tags.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleRemoveCustomer(customer)}
                      >
                        <X size={18} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel_Btn" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={confirmRemoveCustomer}
        title="Remove Customer from Tag"
        description={`Are you sure you want to remove "${
          customerToRemove?.first_name || "this customer"
        }" from "${tag?.display_name}"?`}
      />
    </>
  );
};

export default ViewTagCustomersModal;

////code before adding excel export feature
// import React, { useState, useEffect } from "react";
// import {
//   Modal,
//   ModalHeader,
//   ModalBody,
//   ModalFooter,
//   Button,
//   Spinner,
//   Badge,
//   Input,
// } from "reactstrap";
// import { Users, X, Trash2 } from "react-feather";
// import axios from "axios";
// import { baseURL } from "../../../../../Services/api/baseURL";
// import { toast } from "react-toastify";
// import ConfirmModal from "../../../../ConfirmModal/ConfirmModal1";

// const ViewTagCustomersModal = ({ isOpen, toggle, tag, onSuccess }) => {
//   const [customers, setCustomers] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [selectedCustomers, setSelectedCustomers] = useState([]);
//   const [customerToRemove, setCustomerToRemove] = useState(null);
//   const [confirmModalOpen, setConfirmModalOpen] = useState(false);

//   useEffect(() => {
//     if (isOpen && tag) {
//       fetchCustomers();
//       setSelectedCustomers([]);
//     }
//   }, [isOpen, tag]);

//   const fetchCustomers = async () => {
//     setIsLoading(true);
//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       const response = await axios.get(
//         `${baseURL}/api/tags/${tag._id}/customers`,
//         {
//           params: { limit: 100 },
//           headers: { Authorization: token },
//         }
//       );

//       if (response.status === 200) {
//         setCustomers(response.data.data.customers);
//       }
//     } catch (error) {
//       console.error("Failed to fetch customers:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleRemoveCustomer = async (customer) => {
//     setCustomerToRemove(customer);
//     setConfirmModalOpen(true);
//   };

//   const confirmRemoveCustomer = async () => {
//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       await axios.post(
//         `${baseURL}/api/tags/customers/${customerToRemove._id}/remove`,
//         { tagIds: [tag._id] },
//         { headers: { Authorization: token } }
//       );

//       toast.success("Customer removed from tag");
//       setCustomers((prev) =>
//         prev.filter((c) => c._id !== customerToRemove._id)
//       );
//       if (onSuccess) onSuccess();
//       setConfirmModalOpen(false);
//       setCustomerToRemove(null);
//     } catch (error) {
//       toast.error("Failed to remove customer");
//       console.error(error);
//     }
//   };

//   const handleBulkRemove = async () => {
//     if (selectedCustomers.length === 0) {
//       toast.error("Please select customers to remove");
//       return;
//     }

//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       const response = await axios.post(
//         `${baseURL}/api/tags/bulk/remove`,
//         {
//           customerIds: selectedCustomers,
//           tagId: tag._id,
//         },
//         { headers: { Authorization: token } }
//       );

//       if (response.status === 200) {
//         toast.success(
//           `Tag removed from ${response.data.data.modified} customer(s)`
//         );
//         fetchCustomers();
//         setSelectedCustomers([]);
//         if (onSuccess) onSuccess();
//       }
//     } catch (error) {
//       toast.error("Failed to remove customers");
//       console.error(error);
//     }
//   };

//   const handleSelectCustomer = (customerId) => {
//     setSelectedCustomers((prev) => {
//       if (prev.includes(customerId)) {
//         return prev.filter((id) => id !== customerId);
//       } else {
//         return [...prev, customerId];
//       }
//     });
//   };

//   const handleSelectAll = () => {
//     if (selectedCustomers.length === customers.length) {
//       setSelectedCustomers([]);
//     } else {
//       setSelectedCustomers(customers.map((c) => c._id));
//     }
//   };

//   return (
//     <>
//       <Modal isOpen={isOpen} toggle={toggle} size="lg">
//         <ModalHeader toggle={toggle}>
//           <div className="d-flex align-items-center gap-2">
//             <Users size={20} />
//             <span>
//               Customers with "{tag?.display_name}"
//               {!isLoading && (
//                 <Badge color="light" className="ms-2">
//                   {customers.length}
//                 </Badge>
//               )}
//             </span>
//           </div>
//         </ModalHeader>
//         <ModalBody>
//           {selectedCustomers.length > 0 && (
//             <div className="mb-3 d-flex justify-content-between align-items-center">
//               <Badge color="primary" style={{ fontSize: "0.9rem", padding: "8px 12px" }}>
//                 {selectedCustomers.length} selected
//               </Badge>
//               <Button
//                 color="danger"
//                 size="sm"
//                 onClick={handleBulkRemove}
//               >
//                 <Trash2 size={14} className="me-1" />
//                 Remove Selected
//               </Button>
//             </div>
//           )}

//           {customers.length > 0 && (
//             <div className="mb-3">
//               <label>
//                 <Input
//                   type="checkbox"
//                   checked={customers.length > 0 && selectedCustomers.length === customers.length}
//                   onChange={handleSelectAll}
//                   className="me-2"
//                 />
//                 Select All
//               </label>
//             </div>
//           )}

//           <div
//             style={{
//               maxHeight: "500px",
//               overflowY: "auto",
//               border: "1px solid #e0e0e0",
//               borderRadius: "8px",
//             }}
//           >
//             {isLoading ? (
//               <div className="text-center py-4">
//                 <Spinner color="primary" />
//               </div>
//             ) : customers.length === 0 ? (
//               <div className="text-center py-4 text-muted">
//                 No customers have this tag yet
//               </div>
//             ) : (
//               customers.map((customer) => {
//                 const fullName =
//                   customer.first_name && customer.last_name
//                     ? `${customer.first_name} ${customer.last_name}`
//                     : customer.first_name || customer.last_name || "N/A";
//                 const isSelected = selectedCustomers.includes(customer._id);

//                 return (
//                   <div
//                     key={customer._id}
//                     style={{
//                       padding: "12px 16px",
//                       borderBottom: "1px solid #e0e0e0",
//                       backgroundColor: isSelected ? "#e3f2fd" : "#fff",
//                       transition: "background-color 0.2s",
//                     }}
//                   >
//                     <div className="d-flex align-items-center justify-content-between">
//                       <div className="d-flex align-items-center gap-3 flex-grow-1">
//                         <Input
//                           type="checkbox"
//                           checked={isSelected}
//                           onChange={() => handleSelectCustomer(customer._id)}
//                         />
//                         <div className="flex-grow-1">
//                           <div
//                             style={{ fontWeight: "500", marginBottom: "4px" }}
//                           >
//                             {fullName}
//                           </div>
//                           <div
//                             style={{ fontSize: "0.85rem", color: "#6c757d" }}
//                           >
//                             {customer.email || "No email"}
//                             {customer.phoneNumber && (
//                               <> • {customer.phoneNumber}</>
//                             )}
//                           </div>
//                           {customer.tags && customer.tags.length > 1 && (
//                             <div className="mt-2">
//                               {customer.tags
//                                 .filter((t) => t._id !== tag._id)
//                                 .slice(0, 3)
//                                 .map((t) => (
//                                   <Badge
//                                     key={t._id}
//                                     style={{
//                                       backgroundColor: t.color,
//                                       color: "#fff",
//                                       marginRight: "4px",
//                                       fontSize: "0.75rem",
//                                     }}
//                                   >
//                                     {t.display_name}
//                                   </Badge>
//                                 ))}
//                               {customer.tags.length > 4 && (
//                                 <Badge color="light">
//                                   +{customer.tags.length - 4}
//                                 </Badge>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                       <Button
//                         color="link"
//                         size="sm"
//                         className="text-danger"
//                         onClick={() => handleRemoveCustomer(customer)}
//                       >
//                         <X size={18} />
//                       </Button>
//                     </div>
//                   </div>
//                 );
//               })
//             )}
//           </div>
//         </ModalBody>
//         <ModalFooter>
//           <Button className="cancel_Btn" onClick={toggle}>
//             Close
//           </Button>
//         </ModalFooter>
//       </Modal>

//       <ConfirmModal
//         isOpen={confirmModalOpen}
//         onClose={() => setConfirmModalOpen(false)}
//         onConfirm={confirmRemoveCustomer}
//         title="Remove Customer from Tag"
//         description={`Are you sure you want to remove "${
//           customerToRemove?.first_name || "this customer"
//         }" from "${tag?.display_name}"?`}
//       />
//     </>
//   );
// };

// export default ViewTagCustomersModal;